import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
  Upload,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import FilterDrawer from '../components/FilterDrawer';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  agentPriceApi,
  carrierApi,
  saveBlob,
  AgentPriceAgentOption,
  AgentPriceImportBatchDTO,
  AgentPriceImportPreviewDTO,
  AgentPriceItemDTO,
  AgentPriceSamplingTaskDTO,
  AgentPriceSheetDTO,
  AgentPriceSheetFilter,
  CarrierDTO,
  ExchangeRateDTO,
} from '../api/kfc';
import { COUNTRY_LIST, countryFilterSort, formatCountryLabel } from '../constants/countries';

const COUNTRY_OPTIONS = COUNTRY_LIST.map((c) => ({ label: formatCountryLabel(c), value: c.code }));
const SOURCE_OPTIONS = [
  { value: 'EXCEL', label: 'Excel 导入' },
  { value: 'API_SAMPLING', label: 'API 采样估算' },
];
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '生效' },
  { value: 'SUPERSEDED', label: '已停用' },
];
const MODE_LABEL: Record<string, string> = { TOTAL: '一口价', PER_KG: '单价', FIRST_ADDITIONAL: '首续重' };
const RUN_STATUS: Record<string, { color: string; text: string }> = {
  RUNNING: { color: 'processing', text: '执行中' },
  SUCCESS: { color: 'success', text: '成功' },
  PARTIAL: { color: 'warning', text: '部分失败' },
  FAILED: { color: 'error', text: '失败' },
};
const fmtTime = (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-');
const errMsg = (e: any, fallback: string) => e?.response?.data?.message || fallback;

function useAgents() {
  const [agents, setAgents] = useState<AgentPriceAgentOption[]>([]);
  useEffect(() => {
    agentPriceApi.agents().then((r) => setAgents(r.data)).catch(() => undefined);
  }, []);
  return agents;
}

// ---------------------------------------------------------------- 价目表

const SheetsTab: React.FC<{ agents: AgentPriceAgentOption[] }> = ({ agents }) => {
  const [filter, setFilter] = useState<AgentPriceSheetFilter>({ status: 'ACTIVE' });
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<AgentPriceSheetDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [itemsSheet, setItemsSheet] = useState<AgentPriceSheetDTO>();
  const [items, setItems] = useState<AgentPriceItemDTO[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const effectiveFilter = useCallback(
    (): AgentPriceSheetFilter => ({
      ...filter,
      from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
      to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
    }),
    [filter, range]
  );

  const load = useCallback(() => {
    setLoading(true);
    agentPriceApi
      .sheets({ ...effectiveFilter(), page, per_page: pageSize })
      .then((r) => {
        setData(r.data.data);
        setTotal(r.data.total);
      })
      .catch((e) => message.error(errMsg(e, '加载价目表失败')))
      .finally(() => setLoading(false));
  }, [effectiveFilter, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const openItems = (s: AgentPriceSheetDTO) => {
    setItemsSheet(s);
    setItemsLoading(true);
    agentPriceApi
      .sheetItems(s.id)
      .then((r) => setItems(r.data))
      .catch((e) => message.error(errMsg(e, '加载明细失败')))
      .finally(() => setItemsLoading(false));
  };

  const supersede = (s: AgentPriceSheetDTO) =>
    agentPriceApi
      .supersedeSheet(s.id)
      .then(() => {
        message.success('已停用');
        load();
      })
      .catch((e) => message.error(errMsg(e, '停用失败')));

  const exportSheets = () =>
    agentPriceApi
      .exportSheets(effectiveFilter())
      .then((r) => saveBlob(r.data, `代理价目表-${dayjs().format('YYYYMMDD')}.xlsx`))
      .catch(() => message.error('导出失败'));

  const priceText = (it: AgentPriceItemDTO) => {
    switch (it.pricingMode) {
      case 'TOTAL':
        return `${it.totalPrice}`;
      case 'FIRST_ADDITIONAL':
        return `首重 ${it.firstWeight}KG ${it.firstPrice} + 每 ${it.additionalUnit}KG ${it.additionalPrice}`;
      default:
        return `${it.unitPrice} /KG`;
    }
  };

  return (
    <div>
      <FilterDrawer variant="space" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="代理"
          style={{ width: 180 }}
          value={filter.agentId}
          onChange={(v) => {
            setPage(1);
            setFilter((f) => ({ ...f, agentId: v }));
          }}
          options={agents.map((a) => ({ value: a.agentId, label: `${a.agentName} (${a.agentId})` }))}
        />
        <Input.Search
          allowClear
          placeholder="渠道代码 / 名称"
          style={{ width: 200 }}
          onSearch={(v) => {
            setPage(1);
            setFilter((f) => ({ ...f, keyword: v || undefined }));
          }}
        />
        <Select
          allowClear
          placeholder="来源"
          style={{ width: 140 }}
          value={filter.source}
          options={SOURCE_OPTIONS}
          onChange={(v) => {
            setPage(1);
            setFilter((f) => ({ ...f, source: v }));
          }}
        />
        <Select
          allowClear
          placeholder="状态"
          style={{ width: 110 }}
          value={filter.status}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setPage(1);
            setFilter((f) => ({ ...f, status: v }));
          }}
        />
        <DatePicker.RangePicker
          value={range}
          placeholder={['生效日期起', '生效日期止']}
          onChange={(v) => {
            setPage(1);
            setRange(v as [Dayjs | null, Dayjs | null] | null);
          }}
        />
        <Button icon={<DownloadOutlined />} onClick={exportSheets}>
          导出 Excel
        </Button>
      </FilterDrawer>
      <AdaptiveTable<AgentPriceSheetDTO>
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={data}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 张`,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        columns={[
          { title: '代理', dataIndex: 'agentName', width: 140, render: (v, r) => `${v || r.agentId} (${r.agentId})` },
          { title: '代理渠道代码', dataIndex: 'agentChannelCode', width: 140 },
          { title: '渠道名称', dataIndex: 'channelName' },
          { title: '生效日期', dataIndex: 'effectiveDate', width: 110 },
          { title: '币种', dataIndex: 'currency', width: 70 },
          { title: '材积除以数', dataIndex: 'volumeDivisor', width: 100 },
          {
            title: '来源',
            dataIndex: 'source',
            width: 120,
            render: (v: string) => <Tag color={v === 'EXCEL' ? 'blue' : 'gold'}>{v === 'EXCEL' ? 'Excel' : 'API估算'}</Tag>,
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (v: string) => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{v === 'ACTIVE' ? '生效' : '已停用'}</Tag>,
          },
          { title: '明细行', dataIndex: 'itemCount', width: 80 },
          { title: '导入人', dataIndex: 'createdBy', width: 140, ellipsis: true },
          { title: '创建时间', dataIndex: 'createdAt', width: 140, render: fmtTime },
          {
            title: '操作',
            key: 'actions',
            width: 120,
            render: (_, r) => (
              <Space>
                <Typography.Link onClick={() => openItems(r)}>明细</Typography.Link>
                {r.status === 'ACTIVE' && (
                  <Popconfirm title="停用后历史数据查询不再使用此价目表，确认停用？" onConfirm={() => supersede(r)}>
                    <Typography.Link type="danger">停用</Typography.Link>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={itemsSheet ? `价目明细：${itemsSheet.agentId} / ${itemsSheet.channelName ?? itemsSheet.agentChannelCode}（${itemsSheet.effectiveDate}，${itemsSheet.currency}）` : ''}
        open={!!itemsSheet}
        footer={null}
        width={1100}
        onCancel={() => setItemsSheet(undefined)}
      >
        <AdaptiveTable<AgentPriceItemDTO>
          size="small"
          rowKey="id"
          loading={itemsLoading}
          dataSource={items}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ y: 520 }}
          columns={[
            { title: '国家', dataIndex: 'countryCode', width: 70 },
            { title: '分区', dataIndex: 'zoneCode', width: 80, render: (v) => v || '-' },
            { title: '包装', dataIndex: 'packing', width: 70, render: (v) => v || '不限' },
            { title: '货物类型', dataIndex: 'goodsTypes', width: 100, render: (v?: string[]) => (v?.length ? v.join(',') : '普货') },
            { title: '重量段(KG)', key: 'band', width: 120, render: (_, r) => `(${r.weightMin}, ${r.weightMax}]` },
            { title: '计价方式', dataIndex: 'pricingMode', width: 90, render: (v: string) => MODE_LABEL[v] ?? v },
            { title: '价格', key: 'price', render: (_, r) => priceText(r) },
            { title: '最低收费', dataIndex: 'minCharge', width: 90, render: (v) => v ?? '-' },
            { title: '燃油%', dataIndex: 'fuelRate', width: 70, render: (v) => v ?? '-' },
            {
              title: '附加费',
              dataIndex: 'otherFees',
              render: (v?: AgentPriceItemDTO['otherFees']) =>
                v?.length ? v.map((f) => `${f.name}:${f.amount}/${f.mode === 'PER_KG' ? 'KG' : '票'}`).join('；') : '-',
            },
            { title: '估算', dataIndex: 'estimated', width: 60, render: (v) => (v ? <Tag color="gold">估算</Tag> : '') },
          ]}
        />
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------- 导入

const ImportTab: React.FC = () => {
  const [preview, setPreview] = useState<AgentPriceImportPreviewDTO>();
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [batches, setBatches] = useState<AgentPriceImportBatchDTO[]>([]);

  const loadBatches = useCallback(() => {
    agentPriceApi.importBatches().then((r) => setBatches(r.data)).catch(() => undefined);
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const upload = (file: File) => {
    setUploading(true);
    setPreview(undefined);
    agentPriceApi
      .importPreview(file)
      .then((r) => setPreview(r.data))
      .catch((e) => message.error(errMsg(e, '解析失败')))
      .finally(() => setUploading(false));
    return false;
  };

  const confirm = () => {
    if (!preview?.previewId) return;
    setConfirming(true);
    agentPriceApi
      .importConfirm(preview.previewId)
      .then((r) => {
        message.success(`导入完成：${r.data.sheetCount} 张价目表，${r.data.itemCount} 行明细`);
        setPreview(undefined);
        loadBatches();
      })
      .catch((e) => message.error(errMsg(e, '导入失败')))
      .finally(() => setConfirming(false));
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button
          icon={<DownloadOutlined />}
          onClick={() =>
            agentPriceApi
              .downloadTemplate()
              .then((r) => saveBlob(r.data, '代理价目表导入模板.xlsx'))
              .catch(() => message.error('下载模板失败'))
          }
        >
          下载模板
        </Button>
        <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={upload}>
          <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
            上传预览
          </Button>
        </Upload>
        <Typography.Text type="secondary">
          同一代理、渠道、生效日期重复导入时，旧价目表自动停用
        </Typography.Text>
      </Space>

      {preview && (
        <Card size="small" title={`预览：${preview.fileName ?? ''}`} style={{ marginBottom: 12 }}>
          <Descriptions size="small" column={4} style={{ marginBottom: 8 }}>
            <Descriptions.Item label="价目表">{preview.sheetCount}</Descriptions.Item>
            <Descriptions.Item label="明细行">{preview.itemCount}</Descriptions.Item>
            <Descriptions.Item label="将覆盖同日旧表">{preview.supersedeCount}</Descriptions.Item>
            <Descriptions.Item label="错误 / 警告">
              <Typography.Text type={preview.errors.length ? 'danger' : undefined}>{preview.errors.length}</Typography.Text>
              {' / '}
              <Typography.Text type={preview.warnings.length ? 'warning' : undefined}>{preview.warnings.length}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>
          {preview.errors.length > 0 && (
            <>
              <Alert type="error" showIcon message="存在错误，修正 Excel 后重新上传" style={{ marginBottom: 8 }} />
              <AdaptiveTable
                size="small"
                rowKey={(r, i) => `e${i}`}
                dataSource={preview.errors}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                columns={[
                  { title: '行号', dataIndex: 'row', width: 80 },
                  { title: '错误', dataIndex: 'message' },
                ]}
                style={{ marginBottom: 8 }}
              />
            </>
          )}
          {preview.warnings.length > 0 && (
            <AdaptiveTable
              size="small"
              rowKey={(r, i) => `w${i}`}
              dataSource={preview.warnings}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              columns={[
                { title: '行号', dataIndex: 'row', width: 80 },
                { title: '警告', dataIndex: 'message', render: (v) => <Typography.Text type="warning">{v}</Typography.Text> },
              ]}
              style={{ marginBottom: 8 }}
            />
          )}
          <AdaptiveTable
            size="small"
            rowKey={(r) => `${r.agentId}::${r.agentChannelCode}::${r.effectiveDate}`}
            dataSource={preview.sheets}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            columns={[
              { title: '代理', dataIndex: 'agentId', width: 90 },
              { title: '代理渠道代码', dataIndex: 'agentChannelCode', width: 140 },
              { title: '渠道名称', dataIndex: 'channelName' },
              { title: '生效日期', dataIndex: 'effectiveDate', width: 110 },
              { title: '币种', dataIndex: 'currency', width: 70 },
              { title: '明细行', dataIndex: 'itemCount', width: 80 },
              {
                title: '覆盖旧表',
                dataIndex: 'replaceExisting',
                width: 90,
                render: (v: boolean) => (v ? <Tag color="orange">覆盖</Tag> : '-'),
              },
            ]}
          />
          <Space style={{ marginTop: 8 }}>
            <Button type="primary" disabled={!preview.previewId} loading={confirming} onClick={confirm}>
              确认导入
            </Button>
            <Button onClick={() => setPreview(undefined)}>取消</Button>
          </Space>
        </Card>
      )}

      <Card size="small" title="导入批次">
        <AdaptiveTable<AgentPriceImportBatchDTO>
          size="small"
          rowKey="id"
          dataSource={batches}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={[
            { title: '批次号', dataIndex: 'id', width: 220 },
            { title: '文件名', dataIndex: 'fileName' },
            { title: '导入人', dataIndex: 'createdBy', width: 180 },
            { title: '时间', dataIndex: 'createdAt', width: 150, render: fmtTime },
            { title: '价目表数', dataIndex: 'sheetCount', width: 90 },
            { title: '明细行数', dataIndex: 'itemCount', width: 90 },
            { title: '覆盖旧表', dataIndex: 'supersededCount', width: 90 },
          ]}
        />
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------- 采样任务

interface TaskFormValues {
  name: string;
  agentIds?: string[];
  countryCodes: string[];
  weightPoints: string[];
  packing: string;
  goodsTypes?: string[];
  enabled: boolean;
}

const SamplingTab: React.FC<{ agents: AgentPriceAgentOption[] }> = ({ agents }) => {
  const [tasks, setTasks] = useState<AgentPriceSamplingTaskDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<AgentPriceSamplingTaskDTO | null>(null);
  const [editInitial, setEditInitial] = useState<Record<string, unknown>>({});
  const [form] = Form.useForm<TaskFormValues>();

  const load = useCallback(() => {
    setLoading(true);
    agentPriceApi
      .samplingTasks()
      .then((r) => setTasks(r.data))
      .catch((e) => message.error(errMsg(e, '加载采样任务失败')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const anyRunning = tasks.some((t) => t.lastRunStatus === 'RUNNING');
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, [anyRunning, load]);

  const openEdit = (t: AgentPriceSamplingTaskDTO | null) => {
    setEditInitial(
      t
        ? {
            name: t.name,
            agentIds: t.agentIds,
            countryCodes: t.countryCodes,
            weightPoints: (t.weightPoints ?? []).map(String),
            packing: t.packing ?? 'WPX',
            goodsTypes: t.goodsTypes,
            enabled: t.enabled !== false,
          }
        : {
            name: '',
            agentIds: [],
            countryCodes: ['US', 'GB', 'DE', 'CA', 'AU', 'JP'],
            weightPoints: ['0.5', '1', '2', '5', '10', '21', '45', '71', '100'],
            packing: 'WPX',
            goodsTypes: [],
            enabled: true,
          }
    );
    setEditing(t ?? ({} as AgentPriceSamplingTaskDTO));
  };

  const save = async () => {
    const v = await form.validateFields();
    const points = v.weightPoints.map((p) => Number(p));
    if (points.some((p) => !Number.isFinite(p) || p <= 0)) {
      message.error('重量点须为正数');
      return;
    }
    const dto: AgentPriceSamplingTaskDTO = { ...v, weightPoints: points };
    try {
      if (editing?.id) await agentPriceApi.updateSamplingTask(editing.id, dto);
      else await agentPriceApi.createSamplingTask(dto);
      message.success('已保存');
      setEditing(null);
      load();
    } catch (e) {
      message.error(errMsg(e, '保存失败'));
    }
  };

  const toggle = (t: AgentPriceSamplingTaskDTO, enabled: boolean) =>
    agentPriceApi
      .updateSamplingTask(t.id!, { ...t, enabled })
      .then(load)
      .catch((e) => message.error(errMsg(e, '更新失败')));

  const run = (t: AgentPriceSamplingTaskDTO) =>
    agentPriceApi
      .runSamplingTask(t.id!)
      .then(() => {
        message.success('已开始执行，完成后刷新查看结果');
        load();
      })
      .catch((e) => message.error(errMsg(e, '执行失败')));

  const remove = (t: AgentPriceSamplingTaskDTO) =>
    agentPriceApi
      .deleteSamplingTask(t.id!)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(errMsg(e, '删除失败')));

  const agentName = (id?: string) => agents.find((a) => a.agentId === id)?.agentName ?? id ?? '-';

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={() => openEdit(null)}>
          新增采样任务
        </Button>
        <Button onClick={load}>刷新</Button>
        <Typography.Text type="secondary">
          启用的任务每天 03:00 自动执行；只调用代理查价接口，已有 Excel 价目表的渠道不生成估算价目表
        </Typography.Text>
      </Space>
      <AdaptiveTable<AgentPriceSamplingTaskDTO>
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={tasks}
        pagination={false}
        expandable={{
          rowExpandable: (r) => !!r.lastRunSummary?.length,
          expandedRowRender: (r) => (
            <AdaptiveTable
              size="small"
              rowKey={(s, i) => `${s.agentId ?? ''}${i}`}
              pagination={false}
              dataSource={r.lastRunSummary ?? []}
              columns={[
                { title: '代理', dataIndex: 'agentId', render: (v) => agentName(v) },
                { title: '调用次数', dataIndex: 'calls' },
                { title: '失败次数', dataIndex: 'failedCalls' },
                { title: '报价条数', dataIndex: 'quotes' },
                { title: '生成估算价目表', dataIndex: 'sheets' },
                { title: '跳过(已有Excel)', dataIndex: 'skippedExcelChannels' },
                { title: '信息', dataIndex: 'message', render: (v) => v || '-' },
              ]}
            />
          ),
        }}
        columns={[
          { title: '任务名', dataIndex: 'name', width: 160 },
          {
            title: '代理',
            dataIndex: 'agentIds',
            render: (v?: string[]) => (v?.length ? v.map((a) => <Tag key={a}>{agentName(a)}</Tag>) : '全部支持查价的代理'),
          },
          { title: '国家', dataIndex: 'countryCodes', render: (v: string[]) => v?.join(', ') },
          { title: '重量点(KG)', dataIndex: 'weightPoints', render: (v: number[]) => v?.join(' / ') },
          { title: '包装', dataIndex: 'packing', width: 70 },
          {
            title: '启用',
            dataIndex: 'enabled',
            width: 70,
            render: (v: boolean, r) => <Switch size="small" checked={v !== false} onChange={(c) => toggle(r, c)} />,
          },
          { title: '最近执行', dataIndex: 'lastRunAt', width: 140, render: fmtTime },
          {
            title: '结果',
            dataIndex: 'lastRunStatus',
            width: 100,
            render: (v?: string) => (v ? <Tag color={RUN_STATUS[v]?.color}>{RUN_STATUS[v]?.text ?? v}</Tag> : '-'),
          },
          {
            title: '操作',
            key: 'actions',
            width: 170,
            render: (_, r) => (
              <Space>
                <Popconfirm title="立即调用代理查价接口执行采样？" onConfirm={() => run(r)} disabled={r.lastRunStatus === 'RUNNING'}>
                  <Typography.Link disabled={r.lastRunStatus === 'RUNNING'}>立即执行</Typography.Link>
                </Popconfirm>
                <Typography.Link onClick={() => openEdit(r)}>编辑</Typography.Link>
                <Popconfirm title="确认删除该采样任务？" onConfirm={() => remove(r)}>
                  <Typography.Link type="danger">删除</Typography.Link>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={editing?.id ? '编辑采样任务' : '新增采样任务'}
        open={editing !== null}
        onOk={save}
        onCancel={() => setEditing(null)}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} initialValues={editInitial}>
          <Form.Item name="name" label="任务名" rules={[{ required: true, message: '请填写任务名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="agentIds" label="代理" extra="为空表示全部支持查价的代理">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={agents.filter((a) => a.supportsQuote).map((a) => ({ value: a.agentId, label: `${a.agentName} (${a.agentId})` }))}
            />
          </Form.Item>
          <Form.Item name="countryCodes" label="国家" rules={[{ required: true, message: '请选择国家' }]}>
            <Select mode="multiple" showSearch optionFilterProp="label" filterSort={countryFilterSort} options={COUNTRY_OPTIONS} />
          </Form.Item>
          <Form.Item name="weightPoints" label="重量点(KG)" rules={[{ required: true, message: '请填写重量点' }]}>
            <Select mode="tags" tokenSeparators={[',', ' ', '，']} placeholder="输入后回车" />
          </Form.Item>
          <Form.Item name="packing" label="包装类型">
            <Select options={['WPX', 'DOC', 'PAK'].map((p) => ({ value: p, label: p }))} />
          </Form.Item>
          <Form.Item name="goodsTypes" label="货物类型">
            <Select mode="tags" placeholder="默认普货" />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------- 汇率与承运商分类

const ClassifyTab: React.FC = () => {
  const [rates, setRates] = useState<ExchangeRateDTO[]>([]);
  const [carriers, setCarriers] = useState<CarrierDTO[]>([]);
  const [editingRate, setEditingRate] = useState<ExchangeRateDTO | null>(null);
  const [rateInitial, setRateInitial] = useState<Record<string, unknown>>({});
  const [rateForm] = Form.useForm();
  const [drafts, setDrafts] = useState<Record<string, { matchKeywords?: string[]; sortOrder?: number }>>({});

  const loadRates = useCallback(() => {
    agentPriceApi.exchangeRates().then((r) => setRates(r.data)).catch(() => message.error('加载汇率失败'));
  }, []);
  const loadCarriers = useCallback(() => {
    carrierApi.list().then((r) => setCarriers(r.data)).catch(() => message.error('加载承运商失败'));
  }, []);

  useEffect(() => {
    loadRates();
    loadCarriers();
  }, [loadRates, loadCarriers]);

  const openRate = (r: ExchangeRateDTO | null) => {
    setRateInitial(
      r ? { ...r, effectiveDate: dayjs(r.effectiveDate) } : { currency: 'USD', effectiveDate: dayjs(), rateToCny: undefined, remark: '' }
    );
    setEditingRate(r ?? ({} as ExchangeRateDTO));
  };

  const saveRate = async () => {
    const v = await rateForm.validateFields();
    const dto: ExchangeRateDTO = { ...v, effectiveDate: (v.effectiveDate as Dayjs).format('YYYY-MM-DD') };
    try {
      if (editingRate?.id) await agentPriceApi.updateExchangeRate(editingRate.id, dto);
      else await agentPriceApi.createExchangeRate(dto);
      message.success('已保存');
      setEditingRate(null);
      loadRates();
    } catch (e) {
      message.error(errMsg(e, '保存失败'));
    }
  };

  const saveCarrier = (c: CarrierDTO) => {
    const d = drafts[c.id];
    if (!d) return;
    carrierApi
      .update(c.id, { matchKeywords: d.matchKeywords ?? c.matchKeywords ?? [], sortOrder: d.sortOrder ?? c.sortOrder })
      .then(() => {
        message.success('已保存');
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[c.id];
          return next;
        });
        loadCarriers();
      })
      .catch((e) => message.error(errMsg(e, '保存失败')));
  };

  const sortedCarriers = [...carriers].sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));

  return (
    <div>
      <Card
        size="small"
        title="汇率（折算为 CNY）"
        extra={
          <Button size="small" type="primary" onClick={() => openRate(null)}>
            新增汇率
          </Button>
        }
        style={{ marginBottom: 12 }}
      >
        <AdaptiveTable<ExchangeRateDTO>
          size="small"
          rowKey="id"
          dataSource={rates}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={[
            { title: '币种', dataIndex: 'currency', width: 90 },
            { title: '汇率(1 单位 = ? CNY)', dataIndex: 'rateToCny', width: 160 },
            { title: '生效日期', dataIndex: 'effectiveDate', width: 120 },
            { title: '备注', dataIndex: 'remark' },
            { title: '更新时间', dataIndex: 'updatedAt', width: 150, render: fmtTime },
            {
              title: '操作',
              key: 'actions',
              width: 120,
              render: (_, r) => (
                <Space>
                  <Typography.Link onClick={() => openRate(r)}>编辑</Typography.Link>
                  <Popconfirm
                    title="确认删除该汇率？"
                    onConfirm={() =>
                      agentPriceApi
                        .deleteExchangeRate(r.id!)
                        .then(loadRates)
                        .catch((e) => message.error(errMsg(e, '删除失败')))
                    }
                  >
                    <Typography.Link type="danger">删除</Typography.Link>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Card size="small" title="承运商分类（查询价格页的分类按钮）">
        <Typography.Paragraph type="secondary">
          渠道已关联 KFC 渠道承运商时以其为准；否则按关键词（不区分大小写）匹配代理渠道名称，按排序取第一个命中的承运商，都不命中归入“未分类”。
        </Typography.Paragraph>
        <AdaptiveTable<CarrierDTO>
          size="small"
          rowKey="id"
          dataSource={sortedCarriers}
          pagination={false}
          columns={[
            { title: '承运商', dataIndex: 'carrierName', width: 160, render: (v, r) => `${v || r.carrierCode} (${r.carrierCode})` },
            {
              title: '匹配关键词',
              dataIndex: 'matchKeywords',
              render: (_, r) => (
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  tokenSeparators={[',', '，']}
                  placeholder="如 FEDEX, FED, IP, 联邦"
                  value={drafts[r.id]?.matchKeywords ?? r.matchKeywords ?? []}
                  onChange={(v: string[]) => setDrafts((prev) => ({ ...prev, [r.id]: { ...prev[r.id], matchKeywords: v } }))}
                />
              ),
            },
            {
              title: '排序',
              dataIndex: 'sortOrder',
              width: 110,
              render: (_, r) => (
                <InputNumber
                  min={0}
                  precision={0}
                  value={drafts[r.id]?.sortOrder ?? r.sortOrder}
                  onChange={(v) =>
                    setDrafts((prev) => ({ ...prev, [r.id]: { ...prev[r.id], sortOrder: v ?? undefined } }))
                  }
                />
              ),
            },
            {
              title: '操作',
              key: 'actions',
              width: 80,
              render: (_, r) => (
                <Button size="small" type="primary" disabled={!drafts[r.id]} onClick={() => saveCarrier(r)}>
                  保存
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingRate?.id ? '编辑汇率' : '新增汇率'}
        open={editingRate !== null}
        onOk={saveRate}
        onCancel={() => setEditingRate(null)}
        destroyOnClose
      >
        <Form form={rateForm} layout="vertical" preserve={false} initialValues={rateInitial}>
          <Form.Item name="currency" label="币种" rules={[{ required: true, len: 3, message: '3 位币种代码，如 USD' }]}>
            <Input style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item name="rateToCny" label="汇率（1 单位外币 = ? CNY）" rules={[{ required: true, message: '请填写汇率' }]}>
            <InputNumber min={0.000001} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effectiveDate" label="生效日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------- 页面

const TAB_KEYS = ['sheets', 'import', 'sampling', 'classify'];

const AgentPriceDataManage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const agents = useAgents();
  const tab = TAB_KEYS.includes(params.get('tab') ?? '') ? (params.get('tab') as string) : 'sheets';

  return (
    <Card size="small" title="价格数据管理">
      <Tabs
        activeKey={tab}
        onChange={(k) => setParams({ tab: k })}
        destroyInactiveTabPane
        items={[
          { key: 'sheets', label: '价目表', children: <SheetsTab agents={agents} /> },
          { key: 'import', label: '导入', children: <ImportTab /> },
          { key: 'sampling', label: '采样任务', children: <SamplingTab agents={agents} /> },
          { key: 'classify', label: '汇率与承运商分类', children: <ClassifyTab /> },
        ]}
      />
    </Card>
  );
};

export default AgentPriceDataManage;
